import { useState } from 'react';
import { Box, Container, Snackbar, Stack } from '@mui/material';
import {
  AgronodAlert,
  AgronodTypography,
  LoaderLinear,
} from '@agronod/mui-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { TillbakaLank } from '../../components/TillbakaLank';
import { useToken } from '../../utils/hooks';
import {
  ApiError,
  grupperaPerModell,
  INRIKTNING_LABEL,
  mattKolumner,
  modellLabel,
  verifieringApi,
} from '../../utils/api';
import { revisionKeys, useRevisionScope, useRollForKund } from './revisionScope';
import { GardTabell } from './GardTabell';
import { EjUtvaldaTabell } from './EjUtvaldaTabell';
import { IntervallFotnot, SektionRubrik, StatusForklaring } from './StatusForklaring';
import { UrvalsBar } from './UrvalsBar';
import { AntalDialog } from './AntalDialog';
import { BekraftaUrvalDialog } from './BekraftaUrvalDialog';

/**
 * Revisionsledarens vy (kravspec v4 5.3–5.4): pseudonymiserad, rangordnad
 * lista över populationen, antal-steget (låses), urval av exakt det antalet
 * och avpseudonymisering (låser permanent). Efter låsning visas valda gårdar
 * med identitet och övriga pseudonymiserade (C.5). Revisorer skickas till
 * de avpseudonymiserade gårdarna (C.6). 403/409 från api:et visas med
 * serverns meddelande.
 */
const RevisionPage = () => {
  const { scope, basePath } = useRevisionScope();
  const { accessToken } = useToken();
  const queryClient = useQueryClient();
  const {
    roll,
    endastPseudonymiserat,
    kundNamn,
    dataskyddGodkant,
    isPending: rollPending,
  } = useRollForKund(scope?.kundId);

  const [valda, setValda] = useState<Set<number>>(new Set());
  const [bekraftaReveal, setBekraftaReveal] = useState(false);
  const [snack, setSnack] = useState<{
    text: string;
    severity: 'success' | 'error';
  } | null>(null);

  const arLedare = roll === 'REVISIONSLEDARE' && !endastPseudonymiserat;
  const listKey = scope ? revisionKeys.list(scope) : ['revision-list'];
  const { data, isPending, error } = useQuery({
    queryKey: listKey,
    queryFn: () => verifieringApi.getRevisionList(accessToken!, scope!),
    enabled: !!accessToken && !!scope && arLedare && dataskyddGodkant,
    retry: false,
  });

  const sattAntal = useMutation({
    mutationFn: (antal: number) =>
      verifieringApi.setRevisionAntal(accessToken!, { ...scope!, antal }),
    onSuccess: (svar) => {
      setSnack({
        text: `Antalet låstes: ${svar.lastAntal} gårdar ska revideras`,
        severity: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
    onError: (err: Error) => {
      setSnack({ text: err.message, severity: 'error' });
    },
  });

  const avpseudonymisera = useMutation({
    mutationFn: (valdaNummer: number[]) =>
      verifieringApi.revealRevision(accessToken!, { ...scope!, valdaNummer }),
    onSuccess: (svar) => {
      setBekraftaReveal(false);
      setValda(new Set());
      setSnack({
        text: `${svar.gardar.length} gårdar avpseudonymiserades – urvalet är låst`,
        severity: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: listKey });
      void queryClient.invalidateQueries({
        queryKey: revisionKeys.revealed(scope!),
      });
    },
    onError: (err: Error) => {
      setBekraftaReveal(false);
      setSnack({ text: err.message, severity: 'error' });
    },
  });

  if (!scope) {
    return <Navigate to="/" />;
  }
  // AS-4231: dataskyddsvillkoren godkänns i kundvalet innan kunden öppnas.
  if (!rollPending && !dataskyddGodkant) {
    return <Navigate to="/" replace />;
  }
  // Revisorer och läsuppdrag utan identitet (Ö21) ser bara det valda urvalet.
  if (!rollPending && (roll === 'REVISOR' || endastPseudonymiserat)) {
    return <Navigate to={`${basePath}/avpseudonymiserade`} replace />;
  }
  if (!rollPending && roll === undefined) {
    return <Navigate to="/" />;
  }
  if (!accessToken || rollPending || isPending) {
    return <LoaderLinear color="primary" isGlobal={true} />;
  }

  const tillbaka = (
    <TillbakaLank to="/">Tillbaka till alla verifieringsuppdrag</TillbakaLank>
  );

  if (error) {
    // 403 bär affärsregeln (15-dagarsregeln/åtkomst) – informativt, inte fel.
    const severity =
      error instanceof ApiError && error.status === 403 ? 'info' : 'error';
    return (
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {tillbaka}
        <AgronodAlert severity={severity}>{error.message}</AgronodAlert>
      </Container>
    );
  }

  const arLast = data.status === 'AVPSEUDONYMISERAD_LAST';
  const antalAngivet = data.lastAntal !== null;
  const kanValja = !arLast && antalAngivet;
  const detaljUrl = (nummer: number) => `${basePath}/gard/${nummer}`;
  const mattFor = (modell: string | null, gardar: typeof data.gardar) =>
    modell
      ? mattKolumner(
          gardar,
          modell,
          data.modeller.find((m) => m.modell === modell)?.matt,
        )
      : [];
  // Låst urval: de utvalda gårdarna per modell överst, övriga i en gemensam
  // passiv lista. Öppet urval: hela populationen per modell.
  const utvalda = arLast ? data.gardar.filter((g) => g.vald) : [];
  const ejUtvalda = arLast ? data.gardar.filter((g) => !g.vald) : [];
  const sektioner = grupperaPerModell(
    arLast ? utvalda : data.gardar,
    data.modeller,
  ).filter((s) => s.gardar.length > 0);

  // Kryssar i (upp till det låsta antalet) eller ur de angivna gårdarna.
  const valj = (nummer: number[], valj: boolean) => {
    const next = new Set(valda);
    for (const n of nummer) {
      if (!valj) {
        next.delete(n);
      } else if (next.size < (data.lastAntal ?? 0)) {
        next.add(n);
      }
    }
    setValda(next);
  };

  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 8 }}>
      {tillbaka}
      <Box sx={{ mb: 3 }}>
        <AgronodTypography variant="overline" sx={{ color: 'text.secondary' }}>
          {kundNamn}
        </AgronodTypography>
        <AgronodTypography variant="h4" sx={{ mb: 1 }}>
          Revision {INRIKTNING_LABEL[scope.inriktning].toLowerCase()} {scope.ar}
        </AgronodTypography>
        {/* AS-4220: inledningen följer listornas läge – efter låsningen visas
            de utvalda gårdarna med identitet. */}
        <AgronodTypography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {arLast ? 'Avpseudonymiserade' : 'Pseudonymiserade'} listor över
          gårdar som mailats för rättning och
          har minst ett nyckeltal eller en kontrollpunkt utanför förväntat
          intervall, en per modell (
          {data.modeller.map((m) => modellLabel(m.modell)).join(' och ')})
          och sorterade efter avvikelsen mot modellens prediktion, störst
          först. En gård med båda produktionsgrenarna finns i båda listorna
          med samma nummer. Inga värden visas – för varje nyckeltal syns bara
          om gården ligger inom, under eller över förväntat intervall.
        </AgronodTypography>
      </Box>

      {/* Inget omslag: ett sticky-element håller sig inom sin förälder, så
          baren måste ligga direkt i sidans container för att följa med. */}
      {kanValja && data.gardar.length > 0 && (
        <UrvalsBar
          valda={valda.size}
          antal={data.lastAntal ?? 0}
          upptagen={avpseudonymisera.isPending}
          onRensa={() => setValda(new Set())}
          onBekrafta={() => setBekraftaReveal(true)}
        />
      )}

      {data.gardar.length === 0 ? (
        <AgronodAlert severity="info">
          Inga gårdar att revidera: ingen gård som mailats för rättning med
          samtycke för året har något värde utanför förväntat intervall.
        </AgronodAlert>
      ) : (
        <Stack spacing={3}>
          <StatusForklaring />
          {sektioner.map((sektion) => (
            <Box key={sektion.titel}>
              <SektionRubrik
                titel={`${arLast ? 'Utvalda för revision: ' : ''}${sektion.titel} (${sektion.gardar.length})`}
                meta={
                  arLast
                    ? 'Klicka på en gård för att öppna detaljvyn'
                    : kanValja
                      ? 'Klicka på raderna för att välja – shift-klick väljer flera'
                      : undefined
                }
              />
              <GardTabell
                sektion={sektion}
                kolumner={mattFor(sektion.modell, sektion.gardar)}
                detaljUrl={detaljUrl}
                urval={
                  kanValja
                    ? { valda, max: data.lastAntal ?? 0, valj }
                    : undefined
                }
              />
            </Box>
          ))}
          {arLast && ejUtvalda.length > 0 && (
            <Box>
              <SektionRubrik
                titel={`Ej utvalda för revision (${ejUtvalda.length})`}
                meta="Ingår inte i revisionen – förblir pseudonymiserade"
                nedtonad
              />
              <EjUtvaldaTabell gardar={ejUtvalda} modeller={data.modeller} />
            </Box>
          )}
          <IntervallFotnot />
        </Stack>
      )}

      {/* Antal-steget: första besöket, innan antalet låsts. */}
      <AntalDialog
        open={!arLast && !antalAngivet && data.gardar.length > 0}
        total={data.antalGardar}
        upptagen={sattAntal.isPending}
        onBekrafta={(antal) => sattAntal.mutate(antal)}
      />

      <BekraftaUrvalDialog
        open={bekraftaReveal}
        antal={valda.size}
        ovriga={data.antalGardar - valda.size}
        upptagen={avpseudonymisera.isPending}
        onClose={() => setBekraftaReveal(false)}
        onBekrafta={() =>
          avpseudonymisera.mutate([...valda].sort((a, b) => a - b))
        }
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
      >
        {/* div: Snackbarens övergång behöver en ref-bärande barnnod */}
        <div>
          {snack && (
            <AgronodAlert severity={snack.severity} onClose={() => setSnack(null)}>
              {snack.text}
            </AgronodAlert>
          )}
        </div>
      </Snackbar>
    </Container>
  );
};

export default RevisionPage;
